/**
 * Who is logged in, in the shape the identify needs: the fields of the auth user that
 * PostHog's identify()/group() send (see usePostHogProvider), so both tools describe the
 * same person the same way.
 */
export interface OpenObserveIdentifiedUser {
  id: string;
  email: string;
  name: string;
  surname: string;
  phone_number: string;
  type: string;
  inserted_at: string;
  picture?: string;
  organization_id?: string;
  business_name?: string;
  organization_source?: string;
  using_ppu_business_model?: boolean;
}

// Same activation condition as initOpenObserveRum: without both, RUM is dormant and there is
// nothing to attach the user to, so the SDK chunk is not even loaded.
function isOpenObserveConfigured(): boolean {
  return Boolean(
    process.env.NEXT_PUBLIC_OPENOBSERVE_RUM_CLIENT_TOKEN && process.env.NEXT_PUBLIC_OPENOBSERVE_RUM_APPLICATION_ID
  );
}

// Browser-only SDKs, loaded lazily for the same SSR reason as in initOpenObserveRum. A call
// made before the SDK has started is buffered by it and applied on start, so the identify
// does not need to wait for init.
async function loadSdks() {
  const { openobserveRum } = await import('@openobserve/browser-rum');
  const { openobserveLogs } = await import('@openobserve/browser-logs');
  return { openobserveRum, openobserveLogs };
}

type OpenObserveSdks = Awaited<ReturnType<typeof loadSdks>>;

// Every identify and clear runs on this one chain, so they reach the SDK in the order they
// were called: a login followed at once by a logout can never end with the user still set.
// The catch keeps the chain alive after a failure, and telemetry never breaks the app.
let pending: Promise<void> = Promise.resolve();

function applyInOrder(apply: (sdks: OpenObserveSdks) => void): void {
  pending = pending
    .then(loadSdks)
    .then(apply)
    .catch(() => undefined);
}

/**
 * Attach the logged-in user to every RUM event, session replay and forwarded error log
 * from now on, so a session in OpenObserve can be found by user id or email.
 *
 * The user carries the same properties PostHog's identify() sets on the person, and the
 * organization becomes the OpenObserve "account", the counterpart of PostHog's
 * `organization` group. Requires the Measurement consent, like the PostHog identify: the
 * caller decides that.
 */
export function identifyOpenObserveUser(user: OpenObserveIdentifiedUser): void {
  if (typeof window === 'undefined' || !isOpenObserveConfigured()) return;

  const usr = {
    id: user.id,
    email: user.email,
    name: user.name,
    first_name: user.name,
    last_name: user.surname,
    created: user.inserted_at,
    avatar: user.picture,
    type: user.type,
    phone: user.phone_number,
    organization_source: user.organization_source,
    using_ppu_business_model: user.using_ppu_business_model,
  };
  const account = user.organization_id
    ? {
        id: user.organization_id,
        name: user.business_name,
        using_ppu_business_model: user.using_ppu_business_model,
      }
    : null;

  applyInOrder(({ openobserveRum, openobserveLogs }) => {
    // Both SDKs, so the error logs forwarded to `_rumlog` carry the same user as the RUM events.
    openobserveRum.setUser(usr);
    openobserveLogs.setUser(usr);
    if (account) {
      openobserveRum.setAccount(account);
      openobserveLogs.setAccount(account);
    } else {
      openobserveRum.clearAccount();
      openobserveLogs.clearAccount();
    }
  });
}

/** Detach the user and organization: on logout, or when the Measurement consent is withdrawn. */
export function clearOpenObserveUser(): void {
  if (typeof window === 'undefined' || !isOpenObserveConfigured()) return;

  applyInOrder(({ openobserveRum, openobserveLogs }) => {
    openobserveRum.clearUser();
    openobserveLogs.clearUser();
    openobserveRum.clearAccount();
    openobserveLogs.clearAccount();
  });
}
